<template>
  <!-- Page Header -->
  <PageHeader 
    title="Mise en avant des produits" 
    subtitle="Ajoutez vos produits en réassort et nouveautés" 
    class="mb-0 pr-10" 
  />
  
  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Jetons restants -->
    <div class="max-w-3xl mx-auto mb-4">
      <div class="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-4 py-3">
        <div class="flex items-center gap-2">
          <i class="fas fa-coins"></i>
          <span class="text-sm">Jetons restants ce mois-ci</span>
        </div>
        <div class="font-semibold">
          {{ remainingJetons }} / {{ monthlyLimit }}
        </div>
      </div>
    </div>

    <!-- Formulaire -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 max-w-3xl mx-auto">
      <h3 class="text-lg font-medium text-gray-900 mb-4">Gestion des MEA de produits</h3>
      
      
      <!-- Checkboxes -->
      <div class="flex gap-6 mb-4">
        <label class="flex items-center gap-3 cursor-pointer">
          <input 
            type="checkbox" 
            v-model="formData.reassort"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          >
          <span class="text-sm font-medium text-gray-700">Reassort</span>
        </label>
        
        <label class="flex items-center gap-3 cursor-pointer">
          <input 
            type="checkbox" 
            v-model="formData.nouveaute"
            class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          >
          <span class="text-sm font-medium text-gray-700">Nouveautés</span>
        </label>
      </div>

      <!-- Champ de référence avec autocomplétion -->
      <div class="relative mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Tapez une Référence base :
        </label>
        <div class="flex items-center gap-3">
          <div class="flex-1 relative">
            <input 
              type="text"
              v-model.trim="newReference"
              @input="handleInput"
              @keydown.enter.prevent="addReference"
              @keydown.down.prevent="selectNext"
              @keydown.up.prevent="selectPrevious"
              @keydown.escape="closeAutocomplete"
              @blur="handleBlur"
              @focus="handleFocus"
              placeholder="Référence base"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <!-- Liste d'autocomplétion -->
            <div 
              v-if="showAutocomplete"
              class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              <!-- Loading state -->
              <div v-if="isSearching" class="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                <i class="fas fa-spinner fa-spin"></i>
                Recherche en cours...
              </div>

              <!-- Error state -->
              <div v-else-if="lastAutocompleteError" class="px-3 py-2 text-sm text-red-600">
                {{ lastAutocompleteError }}
              </div>

              <!-- Results list -->
              <template v-else>
                <div
                  v-for="(result, index) in autocompleteResults"
                  :key="index"
                  @click="selectReference(result.reference_base)"
                  :class="[
                    'px-3 py-2 cursor-pointer hover:bg-blue-50',
                    selectedIndex === index ? 'bg-blue-100' : ''
                  ]"
                >
                  {{ result.reference_base }}
                </div>

                <!-- Empty state -->
                <div v-if="autocompleteResults.length === 0" class="px-3 py-2 text-sm text-gray-500">
                  Aucun résultat
                </div>
              </template>
            </div>
          </div>
          <button 
            @click="addReference"
            :disabled="!newReference.trim() || isLoading"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <i class="fas fa-plus mr-2"></i>
            Ajouter
          </button>
        </div>
      </div>

      <hr class="my-4 border-gray-200" />

      <!-- Bouton de soumission -->
      <div class="flex justify-center">
        <button 
          @click="submitForm"
          :disabled="!canSubmit || isLoading || references.length > remainingJetons || remainingJetons === 0"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <i class="fas fa-check mr-2"></i>
          Ajouter ces produits en {{ typeTextLabel }}
        </button>
      </div>
    </div>

    <!-- Liste des références -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden max-w-3xl mx-auto">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Référence
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="reference in references" :key="reference" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ reference }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button 
                  @click="removeReference(reference)"
                  class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <i class="fas fa-trash mr-1"></i>
                  Supprimer
                </button>
              </td>
            </tr>
            <tr v-if="references.length === 0">
              <td colspan="2" class="px-6 py-8 text-center text-sm text-gray-500">
                Aucune référence ajoutée
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Modal de confirmation -->
  <ModalComp
    v-model:isVisible="showConfirmModal"
    :title="confirmModalTitle"
    :alert="confirmModalMessage"
    cancelText="Annuler"
    confirmText="Confirmer"
    size="sm"
    @confirm="confirmSubmit"
  >
    <template #body>
      <div class="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
        <p class="text-sm font-medium text-gray-700 mb-2">
          Références à mettre en avant ({{ references.length }}) :
        </p>
        <ul class="space-y-1">
          <li 
            v-for="(ref, index) in references" 
            :key="index"
            class="text-sm text-gray-600 flex items-center"
          >
            <i class="fas fa-check-circle text-green-500 mr-2 text-xs"></i>
            {{ ref }}
          </li>
        </ul>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { PageHeader, ModalComp } from '@/components'
import { useNotification } from '@/composables/useNotification'
import type { MeaFormData } from '@/types'
import { 
  getMeaAutocomplete, 
  updateMeaProducts, 
  checkMonthlyMeaBeforeAdd,
} from '@/api/mea'

const { success: showSuccessToast, error: showErrorToast } = useNotification()

// État local
const newReference = ref('')
const references = ref<string[]>([])
const isLoading = ref(false)
const monthlyLimit = ref(100)
const remainingJetons = ref(100)
const autocompleteResults = ref<Array<{ reference_base: string }>>([])
const showAutocomplete = ref(false)
const selectedIndex = ref(-1)
const autocompleteTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const showConfirmModal = ref(false)
const confirmModalTitle = ref('Confirmation des réassorts de produits')
const confirmModalMessage = ref('')
// États pour mieux informer l'utilisateur lors de la saisie
const isSearching = ref(false)
const lastAutocompleteError = ref<string | null>(null)

const formData = reactive<MeaFormData>({
  // Par défaut, sélectionner "Nouveautés" au lieu de "Reassort"
  // Activer également "Reassort" par défaut pour qu'il soit coché automatiquement
  reassort: true,
  nouveaute: true,
  referenceBase: ''
})

const canSubmit = computed(() => {
  return (formData.reassort || formData.nouveaute) && references.value.length > 0
})

// Libellé dynamique pour le bouton selon la sélection
const typeTextLabel = computed(() => {
  if (formData.reassort && formData.nouveaute) return 'réassort et nouveautés'
  if (formData.reassort) return 'réassort'
  return 'nouveautés'
})

// Méthodes d'autocomplétion
const handleInput = async () => {
  if (autocompleteTimeout.value) {
    clearTimeout(autocompleteTimeout.value)
  }

  const term = newReference.value.trim()
  
  if (term.length < 2) {
    autocompleteResults.value = []
    showAutocomplete.value = false
    isSearching.value = false
    lastAutocompleteError.value = null
    return
  }

  autocompleteTimeout.value = setTimeout(async () => {
    try {
      // Démarrer la recherche, afficher le panneau
      isSearching.value = true
      lastAutocompleteError.value = null
      showAutocomplete.value = true
      const referenceString = references.value.join('~')
      const results = await getMeaAutocomplete(term, referenceString)
      autocompleteResults.value = results
      // Toujours afficher le panneau même si 0 résultat pour informer l'utilisateur
      showAutocomplete.value = true
      selectedIndex.value = -1
      isSearching.value = false
    } catch (error) {
      autocompleteResults.value = []
      isSearching.value = false
      // Garder le panneau ouvert avec un message d'erreur
      lastAutocompleteError.value = error instanceof Error ? error.message : 'Erreur lors de la récupération des suggestions'
      showAutocomplete.value = true
    }
  }, 300)
}

const handleFocus = () => {
  const term = newReference.value.trim()
  if (term.length >= 2) {
    // Réouvrir pour afficher résultats/états (chargement/erreur/aucun)
    showAutocomplete.value = true
  }
}

const handleBlur = () => {
  // Délai pour permettre le clic sur les résultats
  setTimeout(() => {
    showAutocomplete.value = false
  }, 200)
}

const closeAutocomplete = () => {
  showAutocomplete.value = false
  selectedIndex.value = -1
}

const selectNext = () => {
  if (selectedIndex.value < autocompleteResults.value.length - 1) {
    selectedIndex.value++
  }
}

const selectPrevious = () => {
  if (selectedIndex.value > 0) {
    selectedIndex.value--
  }
}

const selectReference = (reference: string) => {
  newReference.value = reference
  showAutocomplete.value = false
  selectedIndex.value = -1
}

// Méthodes
const addReference = async () => {
  let ref = newReference.value.trim()

  // Si un élément est sélectionné dans l'autocomplétion, le prendre en priorité
  if (selectedIndex.value >= 0 && autocompleteResults.value[selectedIndex.value]) {
    ref = autocompleteResults.value[selectedIndex.value].reference_base
  }

  if (!ref) return

  // Empêcher les doublons
  if (references.value.includes(ref)) {
    showErrorToast('Cette référence est déjà dans la liste')
    return
  }

  // Valider que la référence existe via l'autocomplétion
  let isValid = false
  if (autocompleteResults.value.some(r => r.reference_base === ref)) {
    isValid = true
  } else {
    try {
      const results = await getMeaAutocomplete(ref, references.value.join('~'))
      if (results.some(r => r.reference_base === ref)) {
        isValid = true
      }
    } catch (e) {
      // En cas d'erreur réseau/auth, laisser le flux d'erreur normal lors du submit
    }
  }

  if (!isValid) {
    showErrorToast("Référence introuvable. Veuillez sélectionner une référence proposée.")
    return
  }

  references.value.push(ref)
  newReference.value = ''
  autocompleteResults.value = []
  showAutocomplete.value = false
  selectedIndex.value = -1
}

const removeReference = (reference: string) => {
  const index = references.value.indexOf(reference)
  if (index > -1) {
    references.value.splice(index, 1)
  }
}

const submitForm = async () => {
  if (!canSubmit.value) return

  isLoading.value = true

  try {
    // Vérifier la limite mensuelle
    const limitCheck = await checkMonthlyMeaBeforeAdd(references.value.length)

    if (!limitCheck.allowed) {
      showErrorToast(`Désolé, il y a une limitation de ${limitCheck.limit} références par mois ! Vous avez déjà ${limitCheck.currentCount} références ce mois-ci. En ajoutant ${references.value.length} nouvelle(s), vous dépasseriez la limite de ${limitCheck.limit}.`)
      isLoading.value = false
      return
    }

    // Mettre à jour l'affichage des jetons restants dans le message
    const willConsume = references.value.length
    const after = Math.max(0, remainingJetons.value - willConsume)

    // Préparer le message de confirmation
    const typeText = formData.reassort && formData.nouveaute 
      ? 'réassort et nouveautés'
      : formData.reassort 
        ? 'réassort'
        : 'nouveautés'
    
    confirmModalMessage.value = `Êtes-vous sûr de vouloir mettre ces ${references.value.length} produit(s) en ${typeText} ?\n\nJetons: ${remainingJetons.value} ➜ ${after}`
    showConfirmModal.value = true
    isLoading.value = false
  } catch (error: any) {
    showErrorToast(error.message || 'Erreur lors de la vérification')
    isLoading.value = false
  }
}

const confirmSubmit = async () => {
  isLoading.value = true

  try {
    // Mettre à jour les produits
    const updated = await updateMeaProducts(references.value, {
      reassort: formData.reassort,
      nouveautes: formData.nouveaute,
    })

    if (updated > 0) {
      showSuccessToast(`${updated} produit(s) mis en avant avec succès !`)
      references.value = []
      newReference.value = ''
      // Rafraîchir les jetons restants
      await refreshRemainingJetons()
    } else {
      showErrorToast('Ces produits ont déjà été mis en avant aujourd\'hui')
    }
  } catch (error: any) {
    showErrorToast(error.message || 'Erreur lors de la mise à jour des produits')
  } finally {
    isLoading.value = false
  }
}

// Nettoyer le timeout au démontage
onUnmounted(() => {
  if (autocompleteTimeout.value) {
    clearTimeout(autocompleteTimeout.value)
  }
})

const refreshRemainingJetons = async () => {
  try {
    const check = await checkMonthlyMeaBeforeAdd(0)
    monthlyLimit.value = check.limit
    remainingJetons.value = Math.max(0, check.limit - check.currentCount)
  } catch (e) {
    remainingJetons.value = monthlyLimit.value
  }
}

onMounted(() => {
  refreshRemainingJetons()
})
</script>

<style scoped>
/* Styles spécifiques si nécessaire */
</style>
